import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plane,
  Building,
  Car,
  Utensils,
  Users,
  Calendar,
  MapPin,
  Hash,
  Clock,
  Trash2,
  ArrowRight,
  Loader2,
  Inbox,
  ChevronDown,
  CheckSquare,
  Square,
  FileText,
  ExternalLink,
  Lock
} from 'lucide-react';
import { Button } from '../ui/button';
import { listUnfilledComponents, assignUnfilledComponent, deleteUnfilledComponent, bulkAssignUnfilledComponents } from '../../services/emailAddressService';
import { useUnfiledComponentEvents, useSSE } from '../../contexts/SSEContext';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { formatDate as formatDateUtil, formatTime as formatTimeUtil, formatTimePreserveTimezone } from '../../utils/dateTimeUtils';

/**
 * Component type icon mapping
 */
const getTypeIcon = (type) => {
  switch (type) {
    case 'commercial_flight':
    case 'private_flight':
    case 'flight':
      return <Plane className="h-4 w-4 text-blue-600" />;
    case 'hotel':
      return <Building className="h-4 w-4 text-green-600" />;
    case 'rental_car':
    case 'car_rental':
      return <Car className="h-4 w-4 text-purple-600" />;
    case 'restaurant':
      return <Utensils className="h-4 w-4 text-orange-600" />;
    case 'meeting':
      return <Users className="h-4 w-4 text-cyan-600" />;
    case 'event':
      return <Calendar className="h-4 w-4 text-pink-600" />;
    default:
      return <Calendar className="h-4 w-4 text-slate-600" />;
  }
};

/**
 * Get display name for component type
 */
const getTypeName = (type) => {
  switch (type) {
    case 'commercial_flight':
      return 'Flight';
    case 'private_flight':
      return 'Private Flight';
    case 'hotel':
      return 'Hotel';
    case 'rental_car':
    case 'car_rental':
      return 'Car Rental';
    case 'restaurant':
      return 'Restaurant';
    case 'meeting':
      return 'Meeting';
    case 'event':
      return 'Event';
    default:
      return type?.replace(/_/g, ' ') || 'Unknown';
  }
};

/**
 * Get type badge color
 */
const getTypeBadgeClass = (type) => {
  switch (type) {
    case 'commercial_flight':
    case 'private_flight':
    case 'flight':
      return 'bg-blue-100 text-blue-700';
    case 'hotel':
      return 'bg-green-100 text-green-700';
    case 'rental_car':
    case 'car_rental':
      return 'bg-purple-100 text-purple-700';
    case 'restaurant':
      return 'bg-orange-100 text-orange-700';
    case 'meeting':
      return 'bg-cyan-100 text-cyan-700';
    case 'event':
      return 'bg-pink-100 text-pink-700';
    default:
      return 'bg-slate-100 text-slate-700';
  }
};


/**
 * UnfiledItemsSection - Displays unfiled components for an executive with ability to assign to trips
 */
const UnfiledItemsSection = ({ executiveId, onItemAssigned }) => {
  const { user } = useAuth();
  const prefs = user?.ui_preferences || { date_input_format: 'US', time_view_format: 'AMPM' };
  const [items, setItems] = useState([]);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTrips, setSelectedTrips] = useState({}); // { componentId: tripId }
  const [assigningId, setAssigningId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  // Bulk selection state
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [bulkTripId, setBulkTripId] = useState('');
  const [isBulkAssigning, setIsBulkAssigning] = useState(false);

  // SSE connection state for refetch on reconnect
  const { connected } = useSSE();
  const prevConnectedRef = useRef(false);

  // Track if initial load is done to avoid showing loading spinner on refetches
  const initialLoadDoneRef = useRef(false);

  // Fetch unfilled components
  const fetchItems = useCallback(async (showLoading = true) => {
    try {
      // Only show loading on initial fetch, not on refetches
      if (showLoading && !initialLoadDoneRef.current) {
        setLoading(true);
      }
      const result = await listUnfilledComponents(executiveId);
      setItems(result.unfilled_components || []);
      initialLoadDoneRef.current = true;
    } catch (err) {
      console.error('Error fetching unfilled components:', err);
      setError('Failed to load unfiled items');
    } finally {
      setLoading(false);
    }
  }, [executiveId]);

  // Fetch trips for the dropdown
  const fetchTrips = useCallback(async () => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.get(`${apiBase}/trips?limit=100`, { headers });
      // API returns array directly, not { trips: [...] }
      setTrips(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching trips:', err);
    }
  }, []);

  useEffect(() => {
    if (executiveId) {
      fetchItems();
      fetchTrips();
    }
  }, [executiveId, fetchItems, fetchTrips]);

  // Refetch items when SSE connection is re-established (to catch missed events)
  useEffect(() => {
    // Only refetch if connection changed from false to true (reconnection)
    // Always refetch on reconnect, regardless of whether executiveId is set
    if (connected && !prevConnectedRef.current) {
      // Small delay to ensure connection is stable
      const timeoutId = setTimeout(() => {
        fetchItems(false); // Don't show loading spinner on refetch
      }, 500);
      return () => clearTimeout(timeoutId);
    }
    prevConnectedRef.current = connected;
  }, [connected, fetchItems]);

  // Handle SSE events for unfiled components (real-time updates)
  const handleUnfiledComponentEvent = useCallback(() => {
    fetchItems();
  }, [fetchItems]);

  useUnfiledComponentEvents(handleUnfiledComponentEvent, [executiveId, onItemAssigned]);

  const handleTripSelect = (componentId, tripId) => {
    setSelectedTrips(prev => ({
      ...prev,
      [componentId]: tripId
    }));
  };

  const handleAssign = async (componentId) => {
    const tripId = selectedTrips[componentId];
    if (!tripId) return;

    setAssigningId(componentId);
    try {
      await assignUnfilledComponent(componentId, tripId);
      // Remove from local state
      setItems(prev => prev.filter(item => item.id !== componentId));
      // Clear selection
      setSelectedTrips(prev => {
        const next = { ...prev };
        delete next[componentId];
        return next;
      });
      // Callback
      if (onItemAssigned) {
        onItemAssigned(componentId, tripId);
      }
    } catch (err) {
      console.error('Error assigning component:', err);
      alert('Failed to assign item to trip');
    } finally {
      setAssigningId(null);
    }
  };

  const handleDelete = async (componentId) => {
    if (!window.confirm('Are you sure you want to delete this unfiled item?')) {
      return;
    }

    setDeletingId(componentId);
    try {
      await deleteUnfilledComponent(componentId);
      setItems(prev => prev.filter(item => item.id !== componentId));
      // Also remove from selected items if it was selected
      setSelectedItems(prev => {
        const next = new Set(prev);
        next.delete(componentId);
        return next;
      });
    } catch (err) {
      console.error('Error deleting component:', err);
      alert('Failed to delete item');
    } finally {
      setDeletingId(null);
    }
  };

  // Bulk selection handlers
  const toggleItemSelection = (componentId) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(componentId)) {
        next.delete(componentId);
      } else {
        next.add(componentId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedItems.size === items.length) {
      // Deselect all
      setSelectedItems(new Set());
    } else {
      // Select all
      setSelectedItems(new Set(items.map(item => item.id)));
    }
  };

  const handleBulkAssign = async () => {
    if (!bulkTripId || selectedItems.size === 0) return;

    setIsBulkAssigning(true);
    try {
      const componentIds = Array.from(selectedItems);
      await bulkAssignUnfilledComponents(componentIds, bulkTripId);
      // Remove assigned items from local state
      setItems(prev => prev.filter(item => !selectedItems.has(item.id)));
      // Clear selections
      setSelectedItems(new Set());
      setBulkTripId('');
      // Callback for each assigned item
      if (onItemAssigned) {
        componentIds.forEach(id => onItemAssigned(id, bulkTripId));
      }
    } catch (err) {
      console.error('Error bulk assigning components:', err);
      alert('Failed to assign items to trip');
    } finally {
      setIsBulkAssigning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6 text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Loading unfiled items...
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-sm text-red-600 py-4">
        {error}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-6 text-slate-500">
        <Inbox className="h-8 w-8 mb-2 text-slate-400" />
        <p className="text-sm">No unfiled items</p>
        <p className="text-xs text-slate-400 mt-1">
          Forwarded confirmations will appear here
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Bulk Action Bar - shows when items are selected */}
      {items.length > 1 && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
          <div className="flex items-center gap-3 flex-wrap">
            {/* Select All checkbox */}
            <button
              onClick={toggleSelectAll}
              className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
            >
              {selectedItems.size === items.length ? (
                <CheckSquare className="h-4 w-4 text-violet-600" />
              ) : (
                <Square className="h-4 w-4" />
              )}
              <span>{selectedItems.size === items.length ? 'Deselect All' : 'Select All'}</span>
            </button>

            {selectedItems.size > 0 && (
              <>
                <span className="text-slate-300">|</span>
                <span className="text-sm text-slate-600">
                  {selectedItems.size} item{selectedItems.size !== 1 ? 's' : ''} selected
                </span>
                <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                  <div className="relative flex-1">
                    <select
                      value={bulkTripId}
                      onChange={(e) => setBulkTripId(e.target.value)}
                      className="w-full h-8 pl-3 pr-8 text-sm border border-slate-200 rounded-md bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 appearance-none"
                    >
                      <option value="">Assign all to trip...</option>
                      {trips.map((trip) => (
                        <option key={trip.id} value={trip.id}>
                          {trip.title || 'Untitled Trip'}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                  </div>
                  <Button
                    size="sm"
                    onClick={handleBulkAssign}
                    disabled={!bulkTripId || isBulkAssigning}
                    className="h-8 bg-violet-600 hover:bg-violet-700 text-white whitespace-nowrap"
                  >
                    {isBulkAssigning ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        Assign All
                        <ArrowRight className="h-3 w-3 ml-1" />
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {items.map((item) => {
        // Debug: Log item data
        if (item.component_type === 'hotel') {
          console.log('🏨 Rendering hotel item:', item.id, 'start_datetime:', item.start_datetime, 'end_datetime:', item.end_datetime);
        }
        return (
        <div
          key={item.id}
          className={`bg-white border rounded-lg p-3 hover:border-slate-300 transition-colors ${
            selectedItems.has(item.id) ? 'border-violet-300 bg-violet-50/30' : 'border-slate-200'
          }`}
        >
          {/* Header Row */}
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {/* Checkbox for bulk selection */}
              {items.length > 1 && (
                <button
                  onClick={() => toggleItemSelection(item.id)}
                  className="flex-shrink-0 text-slate-400 hover:text-violet-600"
                >
                  {selectedItems.has(item.id) ? (
                    <CheckSquare className="h-4 w-4 text-violet-600" />
                  ) : (
                    <Square className="h-4 w-4" />
                  )}
                </button>
              )}
              {getTypeIcon(item.component_type)}
              <h4 className="font-medium text-slate-900 truncate">
                {item.title || 'Untitled'}
              </h4>
              <span className={`text-xs px-2 py-0.5 rounded-full ${getTypeBadgeClass(item.component_type)}`}>
                {getTypeName(item.component_type)}
              </span>
              {item.is_confidential_inbox && (
                <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                  <Lock className="h-3 w-3" />
                  Confidential
                </span>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleDelete(item.id)}
              disabled={deletingId === item.id}
              className="h-7 w-7 text-slate-400 hover:text-red-600 hover:bg-red-50 flex-shrink-0"
            >
              {deletingId === item.id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </Button>
          </div>

          {/* Details Row */}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 mb-3">
            {item.location && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {item.location}
              </span>
            )}
            {item.confirmation_number && (
              <span className="flex items-center gap-1">
                <Hash className="h-3 w-3" />
                {item.confirmation_number}
              </span>
            )}
            {/* For hotels and car rentals, show date range */}
            {(item.component_type === 'hotel' || item.component_type === 'car_rental' || item.component_type === 'rental_car') && item.start_datetime && item.end_datetime ? (
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {formatDateUtil(item.start_datetime, prefs)} - {formatDateUtil(item.end_datetime, prefs)}
              </span>
            ) : item.start_datetime && (
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formatDateUtil(item.start_datetime, prefs)}
                {(formatTimePreserveTimezone(item.start_datetime, prefs) || formatTimeUtil(item.start_datetime, prefs)) &&
                  ` ${formatTimePreserveTimezone(item.start_datetime, prefs) || formatTimeUtil(item.start_datetime, prefs)}`}
              </span>
            )}
            {/* Driver name for car rentals */}
            {(item.component_type === 'car_rental' || item.component_type === 'rental_car') && (
              (() => {
                const cd = item.component_data || {};
                const driverName = cd.driver_name || cd.traveler_name;
                if (driverName) {
                  return (
                    <span className="flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      {driverName}
                    </span>
                  );
                }
                return null;
              })()
            )}
            {/* Passengers for flights */}
            {(item.component_type === 'flight' || item.component_type === 'commercial_flight' || item.component_type === 'private_flight') && (
              (() => {
                const cd = item.component_data || {};
                const passengers = cd.passengers || [];
                const travelerName = cd.traveler_name;
                // Show passengers if available, otherwise show traveler_name
                if (passengers.length > 0) {
                  const passengerNames = passengers.map(p => p.name).filter(Boolean).join(', ');
                  if (passengerNames) {
                    return (
                      <span className="flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        {passengerNames}
                      </span>
                    );
                  }
                } else if (travelerName) {
                  return (
                    <span className="flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      {travelerName}
                    </span>
                  );
                }
                return null;
              })()
            )}
            {/* View Original Document/Email Link */}
            {(() => {
              const cd = item.component_data || {};
              const attachedFiles = cd.attached_files || [];
              if (attachedFiles.length > 0) {
                const file = attachedFiles[0];
                const source = file.source || 'attachment';
                const isEmailBody = source === 'inbound_email_body';

                if (isEmailBody && file.parsingLogId) {
                  return (
                    <a
                      href={`${getApiBase()}/parsing-logs/${file.parsingLogId}/email`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <FileText className="h-3 w-3" />
                      <span>View Email</span>
                      <ExternalLink className="h-2.5 w-2.5" />
                    </a>
                  );
                } else {
                  const docId = file.documentId || file.id;
                  if (docId) {
                    return (
                      <a
                        href={`${getApiBase()}/files/${docId}/view`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FileText className="h-3 w-3" />
                        <span>View Document</span>
                        <ExternalLink className="h-2.5 w-2.5" />
                      </a>
                    );
                  }
                }
              }
              return null;
            })()}
          </div>

          {/* Assign to Trip Row - only show if not selected for bulk */}
          {!selectedItems.has(item.id) && (
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <select
                  value={selectedTrips[item.id] || ''}
                  onChange={(e) => handleTripSelect(item.id, e.target.value)}
                  className="w-full h-8 pl-3 pr-8 text-sm border border-slate-200 rounded-md bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 appearance-none"
                >
                  <option value="">Select a trip...</option>
                  {trips.map((trip) => (
                    <option key={trip.id} value={trip.id}>
                      {trip.title || 'Untitled Trip'}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              </div>
              <Button
                size="sm"
                onClick={() => handleAssign(item.id)}
                disabled={!selectedTrips[item.id] || assigningId === item.id}
                className="h-8 bg-violet-600 hover:bg-violet-700 text-white"
              >
                {assigningId === item.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    Assign
                    <ArrowRight className="h-3 w-3 ml-1" />
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Show "Selected for bulk assign" message when item is selected */}
          {selectedItems.has(item.id) && (
            <div className="text-sm text-violet-600 italic">
              Selected for bulk assignment
            </div>
          )}
        </div>
        );
      })}
    </div>
  );
};

export default UnfiledItemsSection;
